import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Button, Snackbar, Alert, CircularProgress } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { getEvents, getGroupDetail, createEvent } from '../../services/api';
import { EventFormFields, emptyEventForm, hasCoords } from '../Events/eventShared';

const NewEvent = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();

  const [group, setGroup] = useState(null);
  const [form, setForm] = useState(emptyEventForm());
  const [pin, setPin] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Seed the pin from the browser's current location first, since that's
  // where the admin actually is right now when logging a new event. Only
  // fall back to the group's most recent event location if geolocation is
  // unavailable/denied, and a neutral default as the last resort.
  useEffect(() => {
    let cancelled = false;

    getGroupDetail(groupId).then((res) => { if (!cancelled && res.success) setGroup(res.data); }).catch(() => {});

    const fallbackToLastEvent = () => {
      getEvents(groupId).then((res) => {
        if (cancelled || !res.success) return;
        const prev = res.data.find(hasCoords);
        if (prev) setPin([parseFloat(prev.lat), parseFloat(prev.lang)]);
      }).catch(() => {});
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => { if (!cancelled) setPin([pos.coords.latitude, pos.coords.longitude]); },
        () => { if (!cancelled) fallbackToLastEvent(); },
        { enableHighAccuracy: true, timeout: 8000 }
      );
    } else {
      fallbackToLastEvent();
    }

    return () => { cancelled = true; };
  }, [groupId]);

  const handleCreate = async () => {
    if (!form.event_desc.trim()) {
      setError('Enter an event description');
      return;
    }
    if (!pin) {
      setError('Drop a pin on the map to set the event location');
      return;
    }
    setSaving(true);
    try {
      await createEvent(groupId, {
        event_date: form.event_date,
        event_time: form.event_time,
        event_desc: form.event_desc.trim(),
        lat: pin[0],
        lang: pin[1]
      });
      setSuccess('Event created');
      // Back to the Event Library so the new event is visible straight away.
      navigate(`/events/${groupId}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create event');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} gutterBottom>New Event</Typography>
      {group && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {group.name_english}{group.country_name ? ` — ${group.country_name}` : ''}
        </Typography>
      )}

      <Paper sx={{ p: 3, maxWidth: 720 }}>
        <EventFormFields form={form} setForm={setForm} pin={pin} setPin={setPin} disabled={saving} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="contained"
            startIcon={saving ? null : <AddIcon />}
            onClick={handleCreate}
            disabled={saving}
            sx={{ minWidth: 160 }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Create Event'}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={!!success || !!error}
        autoHideDuration={5000}
        onClose={() => { setSuccess(''); setError(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => { setSuccess(''); setError(''); }}
          severity={success ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {success || error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NewEvent;
